import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Bell, 
  BellOff, 
  Trash2, 
  Car, 
  Calendar, 
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle,
  Edit2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;

function ManageReminders() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canAddReminders, setCanAddReminders] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      const response = await axios.get(`${API}/reminders`, { withCredentials: true });
      setReminders(response.data.reminders);
      setCanAddReminders(response.data.can_add_reminders);
    } catch (err) {
      console.error("Error fetching reminders:", err);
      toast.error("Failed to load reminders");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateReminder = async (id, updates) => {
    try {
      await axios.put(`${API}/reminders/${id}`, updates, { withCredentials: true });
      toast.success("Reminder updated");
      fetchReminders();
      setEditingId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update reminder");
    }
  };

  const handleDeleteReminder = async (id, registration) => {
    if (!confirm(`Remove reminder for ${registration}?`)) return;
    
    try {
      await axios.delete(`${API}/reminders/${id}`, { withCredentials: true });
      toast.success("Reminder removed");
      fetchReminders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove reminder");
    }
  };

  const handleUnsubscribeAll = async () => {
    if (!confirm("Disable all reminders? You can re-enable them later.")) return;
    
    try {
      await axios.post(`${API}/reminders/unsubscribe-all`, {}, { withCredentials: true });
      toast.success("All reminders disabled");
      fetchReminders();
    } catch (err) {
      toast.error("Failed to disable reminders");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    try {
      const expiry = new Date(dateStr);
      const today = new Date();
      const diffTime = expiry - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/dashboard">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </Button>
              </a>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Manage Reminders
              </h1>
            </div>
            
            {reminders.length > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleUnsubscribeAll}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                <BellOff className="h-4 w-4 mr-2" />
                Disable All Reminders
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {!canAddReminders && (
          <Card className="mb-6 border-yellow-300 bg-yellow-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="font-medium text-yellow-800">Premium Feature</p>
                  <p className="text-sm text-yellow-700">
                    Reminders are a Premium feature. Your existing reminders will continue for 1 month after your subscription ends.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {reminders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Bell className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-700 mb-2">No Reminders Set</h2>
              <p className="text-gray-500 mb-4">
                You haven&apos;t set up any Tax/MOT expiry reminders yet.
              </p>
              <a href="/dashboard">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  Search a Vehicle to Add Reminder
                </Button>
              </a>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-600">
              You have {reminders.length} vehicle{reminders.length !== 1 ? 's' : ''} with active reminders.
            </p>
            
            {reminders.map((reminder) => {
              const taxDays = getDaysUntil(reminder.tax_expiry_date);
              const motDays = getDaysUntil(reminder.mot_expiry_date);
              
              return (
                <Card key={reminder.id} className={`${!reminder.is_active ? 'opacity-60' : ''}`}>
                  <CardContent className="py-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      {/* Vehicle Info */}
                      <div className="flex items-center gap-4 flex-1">
                        <div className="bg-gray-900 text-white px-3 py-2 rounded font-mono text-lg font-bold">
                          {reminder.registration}
                        </div>
                        <div>
                          <p className="font-medium">
                            {reminder.vehicle_make} {reminder.vehicle_model}
                          </p>
                          <p className="text-sm text-gray-500">{reminder.vehicle_colour}</p>
                        </div>
                      </div>

                      {/* Expiry Dates */}
                      <div className="flex gap-6 text-sm">
                        {reminder.tax_expiry_date && (
                          <div>
                            <p className="text-gray-500">Tax Expires</p>
                            <p className={`font-medium ${taxDays && taxDays < 30 ? 'text-red-600' : ''}`}>
                              {formatDate(reminder.tax_expiry_date)}
                              {taxDays && (
                                <span className="text-xs ml-1">
                                  ({taxDays > 0 ? `${taxDays}d` : 'Expired'})
                                </span>
                              )}
                            </p>
                          </div>
                        )}
                        {reminder.mot_expiry_date && (
                          <div>
                            <p className="text-gray-500">MOT Expires</p>
                            <p className={`font-medium ${motDays && motDays < 30 ? 'text-red-600' : ''}`}>
                              {formatDate(reminder.mot_expiry_date)}
                              {motDays && (
                                <span className="text-xs ml-1">
                                  ({motDays > 0 ? `${motDays}d` : 'Expired'})
                                </span>
                              )}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Reminder Settings */}
                      <div className="flex items-center gap-2">
                        {editingId === reminder.id ? (
                          <Select 
                            defaultValue={String(reminder.reminder_weeks)}
                            onValueChange={(value) => {
                              handleUpdateReminder(reminder.id, { reminder_weeks: parseInt(value) });
                            }}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="10">10 weeks</SelectItem>
                              <SelectItem value="8">8 weeks</SelectItem>
                              <SelectItem value="6">6 weeks</SelectItem>
                              <SelectItem value="4">4 weeks</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="text-sm text-gray-600 flex items-center gap-1">
                            <Bell className="h-4 w-4" />
                            {reminder.reminder_weeks} weeks before
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setEditingId(reminder.id)}
                              className="p-1 h-6 w-6"
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUpdateReminder(reminder.id, { is_active: !reminder.is_active })}
                          className={reminder.is_active ? 'text-green-600' : 'text-gray-400'}
                        >
                          {reminder.is_active ? (
                            <CheckCircle className="h-5 w-5" />
                          ) : (
                            <BellOff className="h-5 w-5" />
                          )}
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteReminder(reminder.id, reminder.registration)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                    
                    {!reminder.is_active && (
                      <p className="text-sm text-gray-500 mt-2 italic">
                        Reminders disabled for this vehicle
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Privacy Notice */}
        <Card className="mt-8 bg-gray-100 border-gray-200">
          <CardContent className="py-4">
            <h3 className="font-medium text-gray-800 mb-2">About Your Data</h3>
            <p className="text-sm text-gray-600">
              We store your vehicle registration and expiry dates to send you reminders. 
              This data is kept securely and only used for the reminder service. 
              You can delete your reminders at any time, and we&apos;ll remove the associated data.
            </p>
            <a href="/privacy" className="text-sm text-blue-600 hover:underline mt-2 inline-block">
              Read our full Privacy Policy →
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ManageReminders;
